/**
 * HTTP client for apps/api (/v1). Guest auth with a device id (plus link attribution),
 * token refresh, and typed calls. Network failures surface as NetworkError so the sync
 * layer can retry later (offline-first); HTTP errors are the adapters' ApiHttpError so
 * `conflictBody` (account-link conflicts, purchase blocks) works on this client too.
 */
import { ApiHttpError } from '@mp/adapters/api';
import type { AnalyticsEvent, AuthResponse } from '@mp/schemas';
import type { RoundBody, Snapshot, SyncOp, SyncReply } from './dto.js';
import { shortId } from './ids.js';
import type { Auth } from './state.js';
import { localDayKey } from './time.js';

export type { Snapshot, SyncOp, SyncReply } from './dto.js';

const enc = encodeURIComponent;

/** Route table in one place; the contract is packages/schemas (api.ts, api-r1.ts). */
export const ROUTES = {
  guest: '/v1/auth/guest',
  refresh: '/v1/auth/refresh',
  me: '/v1/me',
  config: '/v1/config',
  rounds: '/v1/rounds',
  sync: '/v1/sync',
  analytics: '/v1/analytics/batch',
  dailyGift: '/v1/daily-gift/claim',
  offlineCollect: '/v1/offline-sales/collect',
  storeBooster: '/v1/store/booster',
  storeRareItem: '/v1/store/rare-item',
  storeCosmetic: '/v1/store/cosmetic',
  storeSpend: '/v1/store/spend',
  store: '/v1/store',
  /** the store screen's catalog: `store` plus booster packs, coin pouches and decor (older clients ask for `store` and get catalog 1) */
  storeCatalog: '/v1/store?catalog=2',
  costume: '/v1/me/costume',
  costumes: '/v1/costumes',
  skin: '/v1/me/skin',
  skins: '/v1/skins',
  adStart: '/v1/ads/reward/start',
  adComplete: '/v1/ads/reward/complete',
  interstitialLog: '/v1/ads/interstitial/log',
  restockLink: '/v1/links/restock',
  dailyLink: '/v1/links/daily',
  beatLink: '/v1/links/beat',
  daily: (date: string) => `/v1/daily/${enc(date)}`,
  dailyResult: (date: string) => `/v1/daily/${enc(date)}/result`,
  inbox: '/v1/inbox',
  inboxClaim: (id: string) => `/v1/inbox/${enc(id)}/claim`,
  friendInvite: '/v1/friends/invite',
  friendAccept: '/v1/friends/accept',
  friends: '/v1/friends',
  friend: (id: string) => `/v1/friends/${enc(id)}`,
  friendBlock: (id: string) => `/v1/friends/${enc(id)}/block`,
  friendStreet: (id: string) => `/v1/friends/${enc(id)}/street`,
  referrals: '/v1/referrals',
  leagueCurrent: '/v1/league/current',
  leagueResult: (week: number) => `/v1/league/results/${week}`,
  transferToken: '/v1/transfer/token',
  age: '/v1/age',
  consent: '/v1/consent',
  settings: '/v1/settings',
  parentalStart: '/v1/parental/start',
  parentalConfirm: '/v1/parental/confirm',
  parentalRevoke: '/v1/parental/revoke',
  deleteMe: '/v1/me',
  devMailbox: (email: string) => `/v1/dev/mailbox?email=${enc(email)}`,
} as const;

export class NetworkError extends Error {
  override name = 'NetworkError';
}

/**
 * The stored session could not be refreshed (revoked, reused, expired). Never answered with a
 * new guest account (that would orphan the player's progress): the old refresh token is kept
 * and the call counts as offline, so the outbox waits and a later retry may still succeed.
 */
export class AuthStuckError extends NetworkError {
  override name = 'AuthStuckError';
  constructor(readonly code: string) {
    super(`session refresh failed: ${code}`);
  }
}

/** Refresh answers that no retry can fix (apps/api/src/auth/tokens.ts rotateRefreshToken). */
export const SESSION_ENDED: ReadonlySet<string> = new Set(['invalid_refresh_token', 'refresh_token_reused', 'refresh_token_expired', 'account_merged']);
/** How often a dead refresh token is asked about again (a server-side recovery stays possible). */
export const DEAD_RETRY_MS = 5 * 60_000;

/** Calls that add a long-lived way into the account need recent auth (API review C12). */
const RECENT_AUTH = /^\/v1\/(?:auth\/link\/(?!resolve$)[^/]+|auth\/email\/start|transfer\/token)$/;
/** The API's window for a "fresh" session (auth/recent.ts RECENT_AUTH_MS), with a safety margin. */
export const RECENT_AUTH_MS = 10 * 60_000;

/** Non-2xx answer. Extends the adapters' error so their helpers recognise it. */
export class HttpError extends ApiHttpError {
  override name = 'HttpError';
  /** machine-readable `error` field of the API's error body */
  get code(): string {
    const b = this.body as { error?: unknown } | null;
    return b && typeof b.error === 'string' ? b.error : `http_${this.status}`;
  }
}

/** Response check for feature calls: a zod schema from @mp/schemas, or anything with `parse`. */
export interface Parser<T> {
  parse(data: unknown): T;
}

/** A response that did not match the schema the caller expects (a contract bug, not "offline"). */
export class SchemaError extends Error {
  override name = 'SchemaError';
  constructor(
    readonly path: string,
    readonly issues: unknown,
  ) {
    super(`unexpected response from ${path}: ${(issues as Error)?.message ?? String(issues)}`);
  }
}

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** The header apps/api/src/security/idempotency.ts reads (its key shape: 8-64 of `[A-Za-z0-9_-]`). */
export const IDEM_HEADER = 'idempotency-key';
/**
 * The API's answer while a request with the same Idempotency-Key is still running
 * (security/idempotency.ts): the next attempt of the same retry loop gets the first answer.
 */
export const IDEM_IN_PROGRESS = 'request_in_progress';

/** A fresh Idempotency-Key. One per intent, kept across the retries of that one intent. */
export function newIdemKey(): string {
  return shortId(24);
}

export interface RequestOptions<T> {
  body?: unknown;
  /** validate (and type) the response */
  schema?: Parser<T>;
  /**
   * extra attempts on transient failures (network, 5xx, 408, 429) with backoff;
   * default 2 for GET and for a write with an `idemKey`, 0 otherwise
   */
  retries?: number;
  /**
   * `Idempotency-Key` for a write whose answer may be lost on the way back (team writes, trade
   * offers and answers): the API runs it once per (player, route, key) for 24 hours and replays
   * its first 2xx answer, so this is what makes a retry safe. One key per intent — reusing it for
   * a different body is refused as `idempotency_key_reused`.
   */
  idemKey?: string;
  /** false: no auth header (public link views) */
  auth?: boolean;
}

function parseWith<T>(schema: Parser<T> | undefined, path: string, data: unknown): T {
  if (!schema) return data as T;
  try {
    return schema.parse(data);
  } catch (e) {
    throw new SchemaError(path, e);
  }
}

export interface Attribution {
  ref?: string;
  campaign?: string;
}

export interface ApiDeps {
  baseUrl: string;
  fetch: typeof fetch;
  deviceId: () => string;
  locale: () => string;
  platform?: () => 'web' | 'ios' | 'android' | 'discord';
  getAuth: () => Auth | null;
  setAuth: (a: Auth | null) => void;
  /** SO-05: `?ref=&c=` of the link that brought the player, sent once with guest auth */
  attribution?: () => Attribution | null;
  /** where the install came from (Play referrer form), sent with guest auth until it succeeds */
  installReferrer?: () => string | null;
  /** guest creation succeeded: the install referrer is forgotten */
  installReferrerSent?: () => void;
  birthYear?: () => number | null;
  now: () => number;
  timeoutMs?: number;
  /** backoff wait between retries (tests pass a fake) */
  sleep?: (ms: number) => Promise<void>;
  /** a call was refused as `level_outdated` (event rounds: 422 with `currentLevel`), whatever the caller does with it */
  onLevelOutdated?: (body: unknown) => void;
}

export class ApiClient {
  private authing: Promise<Auth> | null = null;
  /** last time a request reached the server (online indicator) */
  lastOkAt = 0;
  /** why the stored session cannot be refreshed right now (null when fine) */
  authProblem: string | null = null;
  /** when the refresh token was last refused for good (sessionEnded) */
  private deadAt = 0;

  constructor(private d: ApiDeps) {}

  get enabled(): boolean {
    return this.d.baseUrl.length > 0;
  }

  get baseUrl(): string {
    return this.d.baseUrl;
  }

  private async raw(method: string, path: string, body?: unknown, token?: string, idemKey?: string): Promise<unknown> {
    if (!this.enabled) throw new NetworkError('api disabled');
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), this.d.timeoutMs ?? 10_000);
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (token) headers.authorization = `Bearer ${token}`;
    if (idemKey) headers[IDEM_HEADER] = idemKey;
    let res: Response;
    try {
      res = await this.d.fetch(this.d.baseUrl + path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: ctrl.signal,
      });
    } catch (e) {
      // offline, DNS, CORS or timeout: callers retry later
      throw new NetworkError((e as Error).message);
    } finally {
      clearTimeout(timer);
    }
    const text = await res.text();
    const isJson = (res.headers.get('content-type') ?? '').includes('json');
    let data: unknown = text || null;
    if (text && isJson) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
    if (res.status >= 500 || res.status === 0) throw new HttpError(res.status, data);
    this.lastOkAt = this.d.now();
    if (!res.ok) {
      const err = new HttpError(res.status, data);
      if (err.code === 'level_outdated') this.d.onLevelOutdated?.(data);
      throw err;
    }
    return data;
  }

  /**
   * SY-01.1: silent guest account (device id + server UUID) on the very first run only. A stored
   * session is refreshed; when that fails it stays (AuthStuckError, retried later), never
   * replaced by a new guest.
   */
  async ensureAuth(): Promise<Auth> {
    const a = this.d.getAuth();
    if (a && a.expiresAt - 30_000 > this.d.now()) return a;
    this.authing ??= (a ? this.refresh(a) : this.guest()).finally(() => (this.authing = null));
    return this.authing;
  }

  /** The access token for other services (realtime hello); `fresh` forces a refresh first. */
  async accessToken(fresh = false): Promise<string> {
    const a = this.d.getAuth();
    if (fresh && a) this.d.setAuth({ ...a, expiresAt: 0 });
    return (await this.ensureAuth()).accessToken;
  }

  /** Adopt a fresh session (account-link resolve, transfer claim). */
  adopt(r: AuthResponse): Auth {
    return this.store(r);
  }

  private store(r: AuthResponse, sessionAt = this.d.now()): Auth {
    const auth: Auth = {
      playerId: r.playerId,
      accessToken: r.accessToken,
      refreshToken: r.refreshToken,
      expiresAt: this.d.now() + r.expiresIn * 1000,
      sessionAt,
    };
    this.d.setAuth(auth);
    this.authProblem = null;
    return auth;
  }

  private async guest(): Promise<Auth> {
    const attr = this.d.attribution?.() ?? null;
    const birthYear = this.d.birthYear?.() ?? null;
    const installReferrer = this.d.installReferrer?.() ?? null;
    const r = (await this.raw('POST', ROUTES.guest, {
      deviceId: this.d.deviceId(),
      platform: this.d.platform?.() ?? 'web',
      locale: this.d.locale().slice(0, 16),
      ...(birthYear ? { birthYear } : {}),
      ...(attr?.ref ? { ref: attr.ref.slice(0, 64) } : {}),
      ...(attr?.campaign ? { campaign: attr.campaign.slice(0, 64) } : {}),
      ...(installReferrer ? { installReferrer: installReferrer.slice(0, 1024) } : {}),
    })) as AuthResponse;
    const auth = this.store(r);
    // after the account exists: a failed first auth is retried with the same referrer
    if (installReferrer) this.d.installReferrerSent?.();
    return auth;
  }

  /**
   * The stored session is gone for good (revoked, reused, expired, merged away): retrying the same
   * refresh token can never succeed. The player gets a way out (SessionEnded: start over, then sign
   * in to recover a linked account) instead of an endless "offline".
   */
  get sessionEnded(): boolean {
    return this.authProblem !== null && SESSION_ENDED.has(this.authProblem);
  }

  private async refresh(a: Auth): Promise<Auth> {
    // a dead token is not re-sent on every call (one ask per DEAD_RETRY_MS keeps a server-side recovery possible)
    if (this.sessionEnded && this.d.now() - this.deadAt < DEAD_RETRY_MS) throw new AuthStuckError(this.authProblem!);
    let r: AuthResponse;
    try {
      // parallel refreshes and lost responses are safe: the API returns the same unused child for 24 h (GRACE_MS)
      r = (await this.raw('POST', ROUTES.refresh, { refreshToken: a.refreshToken })) as AuthResponse;
    } catch (e) {
      if (e instanceof HttpError && e.status < 500) {
        this.authProblem = e.code;
        if (SESSION_ENDED.has(e.code)) this.deadAt = this.d.now();
        throw new AuthStuckError(e.code);
      }
      throw e;
    }
    // same family: the session keeps its start time (recent-auth checks)
    return this.store(r, a.sessionAt ?? 0);
  }

  /**
   * Authenticated call; one retry after re-auth on 401. Satisfies @mp/adapters' ApiClient.
   * With `schema` the response is validated (SchemaError when it does not match). `idemKey` goes
   * out as `Idempotency-Key` (no RECENT_AUTH route is idempotent, so the proof this adds to the
   * body on that path never changes the fingerprint of a key the API is holding an answer for).
   */
  async call<T>(method: string, path: string, body?: unknown, schema?: Parser<T>, idemKey?: string): Promise<T> {
    const auth = await this.ensureAuth();
    const recent = RECENT_AUTH.test(path);
    // sessions older than the API's window prove themselves with the refresh token (C12)
    const withProof = (a: Auth, force = false) =>
      recent && (force || a.sessionAt === undefined || this.d.now() - a.sessionAt > RECENT_AUTH_MS - 60_000) ? { ...(body as object | undefined), refreshToken: a.refreshToken } : body;
    let data: unknown;
    try {
      data = await this.raw(method, path, withProof(auth), auth.accessToken, idemKey);
    } catch (e) {
      if (!(e instanceof HttpError)) throw e;
      if (e.status === 403 && e.code === 'recent_auth_required' && recent) {
        const a = this.d.getAuth() ?? auth;
        data = await this.raw(method, path, withProof(a, true), a.accessToken, idemKey);
        return parseWith(schema, path, data);
      }
      if (e.status !== 401) throw e;
      this.d.setAuth({ ...auth, expiresAt: 0 });
      const again = await this.ensureAuth();
      // the re-auth carries the same key: the API keys its replays per player, and this is the same player
      data = await this.raw(method, path, withProof(again), again.accessToken, idemKey);
    }
    return parseWith(schema, path, data);
  }

  /**
   * Feature-local endpoint call (features keep their routes next to their views instead of
   * in ROUTES): guest auth + one re-auth on 401, retries with backoff on transient failures,
   * then the usual error semantics — `isOffline(e)` (NetworkError, 5xx, 408, 429) means
   * "show the offline state / try later", `errorCode(e)` is the API's refusal code
   * (`feature_disabled`, `not_found`…), SchemaError means the contract drifted.
   */
  async request<T>(method: Method, path: string, opts: RequestOptions<T> = {}): Promise<T> {
    const retries = opts.retries ?? (method === 'GET' || opts.idemKey !== undefined ? 2 : 0);
    let data: unknown;
    for (let attempt = 0; ; attempt++) {
      try {
        data = opts.auth === false ? await this.raw(method, path, opts.body, undefined, opts.idemKey) : await this.call<unknown>(method, path, opts.body, undefined, opts.idemKey);
        break;
      } catch (e) {
        // an Idempotency-Key makes `request_in_progress` worth waiting out: the first attempt of
        // this very loop is still running on the server, and the next one gets its answer
        const retryable = isOffline(e) || (opts.idemKey !== undefined && errorCode(e) === IDEM_IN_PROGRESS);
        if (attempt >= retries || !this.enabled || !retryable) throw e;
        const ms = 400 * 3 ** attempt;
        await (this.d.sleep ?? ((t: number) => new Promise<void>((r) => setTimeout(r, t))))(ms);
      }
    }
    return parseWith(opts.schema, path, data);
  }

  /** Unauthenticated GET (dev mailbox, public link views). */
  publicGet<T>(path: string): Promise<T> {
    return this.raw('GET', path) as Promise<T>;
  }

  /** Unauthenticated POST (transfer claim). */
  publicPost<T>(path: string, body: unknown): Promise<T> {
    return this.raw('POST', path, body) as Promise<T>;
  }

  /**
   * The snapshot. `localDate` is the device's local day (the one the daily-gift claim sends,
   * `localDayKey`), so `meta.dailyGift.claimableToday` answers for exactly that day.
   */
  me(): Promise<Snapshot> {
    return this.call<Snapshot>('GET', `${ROUTES.me}?localDate=${localDayKey(this.d.now())}`);
  }

  sync(ops: SyncOp[]): Promise<SyncReply> {
    return this.call<SyncReply>('POST', ROUTES.sync, { ops });
  }

  submitRound(body: RoundBody): Promise<{ status: string; reason?: string; snapshot: Snapshot }> {
    return this.call('POST', ROUTES.rounds, body);
  }

  config(): Promise<{ remote: Record<string, unknown>; economyVersion: number; levelPacks: Record<string, number> }> {
    return this.call('GET', ROUTES.config);
  }

  analytics(events: AnalyticsEvent[]): Promise<unknown> {
    return this.call('POST', ROUTES.analytics, { events });
  }
}

export function isSnapshot(v: unknown): v is Snapshot {
  return !!v && typeof v === 'object' && 'balances' in v && 'progress' in v && 'player' in v;
}

/** Offline / server unreachable (retry later) as opposed to a refusal. */
export function isOffline(e: unknown): boolean {
  return e instanceof NetworkError || (e instanceof HttpError && (e.status >= 500 || e.status === 408 || e.status === 429));
}

/** The API's error code for a refusal (`rare_item_required`, `ad_cap`…), or null. */
export function errorCode(e: unknown): string | null {
  return e instanceof HttpError ? e.code : null;
}
